import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, MessageCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ModalShell } from "@/components/ui/modal-shell";
import { customFetch } from "@workspace/api-client-react";
import { extractApiErrorMessage } from "@/lib/api-error";
import { CODIGO_CHEGA_EM_30S, ELE_CHEGA_EM_30S } from "@/lib/avisoDoCodigo";

// Conta multi-loja (jul/2026): confirma posse de um WhatsApp que já pertence a
// OUTRA conta antes de usá-lo como número de PEDIDOS desta loja. Aberto quando o
// PUT /settings responde WHATSAPP_NEEDS_VERIFICATION. Reusa o mecanismo de
// código do login: manda 4 dígitos pro número de destino, confirma, e o
// servidor aponta stores.whatsapp_number pra ele (o login não muda).
interface WhatsappVerifyModalProps {
  open: boolean;
  onClose: () => void;
  /** Número que o lojista digitou (bruto/formatado — o server só usa dígitos). */
  whatsappNumber: string;
  whatsappDdi: string;
  /** Confirmado e aplicado no servidor — o pai deve refetch settings + getMe. */
  onVerified: () => void;
  /** "store" (pedidos, padrão) | "login" (troca do WhatsApp de login da conta). */
  target?: "store" | "login";
}

interface RequestCodeResp {
  requiresCode: boolean;
  requestId?: string;
  whatsappFormattedNumber?: string;
}

export function WhatsappVerifyModal({
  open,
  onClose,
  whatsappNumber,
  whatsappDdi,
  onVerified,
  target = "store",
}: WhatsappVerifyModalProps) {
  const isLogin = target === "login";
  const [phase, setPhase] = useState<"sending" | "code" | "confirming">("sending");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [formatted, setFormatted] = useState<string>("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const startedRef = useRef(false);

  const requestCode = useCallback(async () => {
    setPhase("sending");
    setError(null);
    setCode("");
    try {
      const resp = await customFetch<RequestCodeResp>("/api/settings/whatsapp/request-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ whatsappNumber, whatsappDdi, target }),
      });
      if (resp.whatsappFormattedNumber) setFormatted(resp.whatsappFormattedNumber);

      // requiresCode:false = número livre ou já meu (corrida com o 409). Grava
      // direto pelo PUT normal e encerra sem pedir código.
      if (!resp.requiresCode) {
        await customFetch("/api/settings", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            isLogin
              ? { loginWhatsappNumber: whatsappNumber, loginWhatsappDdi: whatsappDdi }
              : { whatsappNumber, whatsappDdi },
          ),
        });
        onVerified();
        return;
      }

      setRequestId(resp.requestId ?? null);
      setPhase("code");
      setResendIn(30);
    } catch (e) {
      setError(extractApiErrorMessage(e, "Não foi possível enviar o código. Tente novamente."));
      setPhase("code");
    }
  }, [whatsappNumber, whatsappDdi, onVerified, target, isLogin]);

  // Dispara o envio ao abrir (uma vez por abertura).
  useEffect(() => {
    if (open && !startedRef.current) {
      startedRef.current = true;
      void requestCode();
    }
    if (!open) {
      startedRef.current = false;
      setPhase("sending");
      setRequestId(null);
      setCode("");
      setError(null);
      setFormatted("");
      setResendIn(0);
    }
  }, [open, requestCode]);

  // Cooldown do reenvio.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const confirm = async () => {
    if (!requestId || code.trim().length < 4 || phase === "confirming") return;
    setPhase("confirming");
    setError(null);
    try {
      await customFetch("/api/settings/whatsapp/confirm-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestId, code: code.trim() }),
      });
      onVerified();
    } catch (e) {
      setError(extractApiErrorMessage(e, "Código incorreto. Tente novamente."));
      setPhase("code");
    }
  };

  return (
    <ModalShell
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isLogin ? "Confirmar WhatsApp de login" : "Confirmar WhatsApp"}
      subtitle={isLogin ? "Vamos enviar um código para o número novo" : "Esse número já está em outra conta — confirme que você tem acesso a ele"}
      size="md"
      footer={
        <>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={confirm}
            disabled={phase !== "code" || code.trim().length < 4}
          >
            {phase === "confirming" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <><ShieldCheck className="w-4 h-4 mr-1" /> Confirmar</>
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {phase === "sending" ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground py-4">
            <Loader2 className="w-5 h-5 animate-spin shrink-0" />
            <div>
              <p>Enviando o código de verificação…</p>
              <p className="mt-1" data-testid="aviso-codigo-30s">{CODIGO_CHEGA_EM_30S}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <MessageCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <p>
                Enviamos um código de 4 dígitos para o WhatsApp
                {formatted ? <> <span className="font-semibold text-foreground">{formatted}</span></> : " informado"}. {ELE_CHEGA_EM_30S}{" "}
                {isLogin ? "Digite-o abaixo para entrar no painel com esse número." : "Digite-o abaixo para receber os pedidos desta loja nele."}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Código</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric"
                placeholder="0000"
                maxLength={4}
                autoFocus
                className="text-center text-2xl tracking-[0.5em] font-semibold"
                onKeyDown={(e) => { if (e.key === "Enter") void confirm(); }}
              />
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              type="button"
              onClick={() => { if (resendIn <= 0) void requestCode(); }}
              disabled={resendIn > 0}
              className="text-sm text-primary underline underline-offset-2 disabled:text-muted-foreground disabled:no-underline"
            >
              {resendIn > 0 ? `Reenviar código em ${resendIn}s` : "Reenviar código"}
            </button>
          </>
        )}
      </div>
    </ModalShell>
  );
}
